import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { clientLicensesAPI } from '../services/api';
import { FiKey, FiCalendar, FiCheck, FiX, FiPlus } from 'react-icons/fi';
import { toast } from 'react-hot-toast';

const Licenses = () => {
  const { user } = useAuth();
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestForm, setRequestForm] = useState({
    requestReason: '',
    agentCount: 1
  });
  const [error, setError] = useState('');

  useEffect(() => {
    if (user?.compID) {
      fetchLicenses();
    }
  }, [user]);

  const fetchLicenses = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await clientLicensesAPI.getLicenses(user.compID);
      setLicenses(data);
    } catch (error) {
      console.error('Error fetching licenses:', error);
      setError('Failed to load licenses. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestLicense = async (e) => {
    e.preventDefault();
    try {
      setError('');
      await clientLicensesAPI.requestLicense(user.compID, requestForm);
      setShowRequestModal(false);
      setRequestForm({ requestReason: '', agentCount: 1 });
      toast.success('License request submitted successfully!');
    } catch (error) {
      console.error('Error requesting license:', error);
      setError('Failed to submit license request. Please try again.');
    }
  };

  const isLicenseExpired = (endDate) => {
    return new Date(endDate) < new Date();
  };

  const isLicenseExpiringSoon = (endDate) => {
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    return new Date(endDate) <= thirtyDaysFromNow && new Date(endDate) >= new Date();
  };

  const getLicenseStatus = (license) => {
    if (isLicenseExpired(license.licenseEnd)) {
      return { status: 'expired', color: 'bg-red-100 text-red-800' };
    }
    if (isLicenseExpiringSoon(license.licenseEnd)) {
      return { status: 'expiring', color: 'bg-yellow-100 text-yellow-800' };
    }
    if (license.isActive) {
      return { status: 'active', color: 'bg-green-100 text-green-800' };
    }
    return { status: 'inactive', color: 'bg-gray-100 text-gray-800' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const activeLicenses = licenses.filter(l => l.isActive && !isLicenseExpired(l.licenseEnd));
  const expiredLicenses = licenses.filter(l => isLicenseExpired(l.licenseEnd));
  const expiringSoon = licenses.filter(l => isLicenseExpiringSoon(l.licenseEnd));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Licenses</h1>
          <p className="mt-2 text-gray-600">
            Manage your AdvDigit software licenses
          </p>
        </div>
        <button 
          onClick={() => setShowRequestModal(true)}
          className="btn-primary flex items-center space-x-2"
        >
          <FiPlus className="h-4 w-4" />
          <span>Request License</span>
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex items-center">
            <div className="text-red-400">
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg mr-3">
              <FiKey className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Total Licenses</p>
              <p className="text-2xl font-semibold text-gray-900">{licenses.length}</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg mr-3">
              <FiCheck className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Active</p>
              <p className="text-2xl font-semibold text-gray-900">{activeLicenses.length}</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg mr-3">
              <svg className="h-6 w-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Total Device Limit</p>
              <p className="text-2xl font-semibold text-gray-900">
                {activeLicenses.reduce((sum, license) => sum + (license.deviceLimit || 0), 0)}
              </p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg mr-3">
              <FiCalendar className="h-6 w-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Expiring Soon</p>
              <p className="text-2xl font-semibold text-gray-900">{expiringSoon.length}</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg mr-3">
              <FiX className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Expired</p>
              <p className="text-2xl font-semibold text-gray-900">{expiredLicenses.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Expiring Soon Alert */}
      {expiringSoon.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <FiCalendar className="h-5 w-5 text-yellow-600 mt-0.5 mr-3" />
            <div>
              <h3 className="text-sm font-medium text-yellow-800">
                {expiringSoon.length} license(s) expiring soon
              </h3>
              <p className="text-sm text-yellow-700 mt-1">
                The following licenses will expire within 30 days. Consider renewing them to avoid service interruption.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Licenses List */}
      {licenses.length === 0 ? (
        <div className="card text-center py-12">
          <FiKey className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Licenses</h3>
          <p className="text-gray-600 mb-6">
            You don't have any licenses yet. Contact support to get started.
          </p>
          <button className="btn-primary">
            Request License
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {licenses.map((license) => {
            const statusInfo = getLicenseStatus(license);
            return (
              <div key={license.licenseID} className="card hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="p-3 bg-blue-100 rounded-lg">
                      <FiKey className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {license.licenseSerial}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {license.assignedToAgent 
                          ? `Assigned to Agent #${license.assignedToAgent}` 
                          : 'Unassigned'
                        }
                      </p>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
                      {statusInfo.status}
                    </span>
                    <p className="text-sm text-gray-500 mt-1">
                      Expires: {new Date(license.licenseEnd).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                
                <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Start Date:</span>
                    <span className="ml-2 text-gray-900">
                      {new Date(license.licenseStart).toLocaleDateString()}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">End Date:</span>
                    <span className="ml-2 text-gray-900">
                      {new Date(license.licenseEnd).toLocaleDateString()}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Device Limit:</span>
                    <span className="ml-2 text-gray-900 font-medium">
                      {license.usedDevices || 0}/{license.deviceLimit || 'N/A'}
                    </span>
                  </div>
                </div>
                
                {/* Device Usage Progress Bar */}
                {license.deviceLimit && (
                  <div className="mt-3">
                    <div className="flex justify-between items-center text-xs text-gray-500 mb-1">
                      <span>Device Usage</span>
                      <span>{Math.round(((license.usedDevices || 0) / license.deviceLimit) * 100)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all ${
                          (license.usedDevices || 0) / license.deviceLimit > 0.8 
                            ? 'bg-red-500' 
                            : (license.usedDevices || 0) / license.deviceLimit > 0.6 
                            ? 'bg-yellow-500' 
                            : 'bg-green-500'
                        }`}
                        style={{ 
                          width: `${Math.min(((license.usedDevices || 0) / license.deviceLimit) * 100, 100)}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Help Section */}
      <div className="card bg-blue-50 border-blue-200">
        <div className="flex items-start">
          <FiKey className="h-5 w-5 text-blue-600 mt-0.5 mr-3" />
          <div>
            <h3 className="text-sm font-medium text-blue-800">Need help with licenses?</h3>
            <p className="text-sm text-blue-700 mt-1">
              Contact our support team for license renewals, upgrades, or any questions about your licensing.
            </p>
            <button className="mt-3 text-sm font-medium text-blue-600 hover:text-blue-500">
              Contact Support →
            </button>
          </div>
        </div>
      </div>

      {/* Request License Modal */}
      {showRequestModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Request New License</h2>
                <button
                  onClick={() => setShowRequestModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleRequestLicense} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason for Request *
                  </label>
                  <textarea
                    value={requestForm.requestReason}
                    onChange={(e) => setRequestForm({ ...requestForm, requestReason: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Explain why you need additional licenses..."
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Number of Agents
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={requestForm.agentCount}
                    onChange={(e) => setRequestForm({ ...requestForm, agentCount: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowRequestModal(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                  >
                    Submit Request
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Licenses;
